import { parseFieldKey, recordLeaseKey, type PresenceUser, type ServerMessage } from '@colplat/protocol';
import type { LeaseClient, LeaseOutcome, Session, Transport } from '@colplat/core-client';
import { escapeHtml, presenceDotHtml } from '@colplat/ui-web';
import { timeAgo } from './webledgerTimeAgo.js';

/**
 * One open form's hold on a record — shared by the short form and the full
 * editor (ADR-0003 as amended 2026-10-22).
 *
 * The two forms grew separate hold handling: the editor's mature, the short
 * form's a thin copy that lost work when a hold lapsed in a background tab.
 * Holds no longer lapse — a hold ends when its holder saves, cancels, closes
 * the page, or is taken over — so what is left to handle is small enough to
 * write once:
 *
 *  - open: take the record, or resume this tab's own hold of it, or find out
 *    who has it and since when;
 *  - taken over while open: tell the form *first*, so it keeps its draft
 *    before anything repaints (`onTaken`);
 *  - freed while watching: take it, and tell the form to reload (`onFreed`);
 *  - the connection goes and comes back: the hold stays this tab's, and the
 *    lease client resumes it;
 *  - before a save: make sure there is a hold to save under;
 *  - the notice that says all of that, in one wording for both forms.
 *
 * What a draft is, how a form repaints and how it shows a kept draft beside
 * the current values stay with each form — those are the parts that differ.
 */

export interface Holder {
  userId: string;
  /** Resolved from presence where possible, so the name and colour are current. */
  person: PresenceUser | undefined;
  since: string | null;
  /** Their tab is connected: "editing now" rather than "unfinished since …". */
  online: boolean;
}

export type HoldView =
  /** This tab holds it: editable. */
  | { kind: 'mine' }
  /** No connection. Editable; `held` says whether the record stays reserved for this tab meanwhile. */
  | { kind: 'offline'; held: boolean }
  /** Somebody else holds it — or this person in another tab (`sameUser`). Read-only, with Take over. */
  | { kind: 'theirs'; holder: Holder; sameUser: boolean }
  /** Refused outright: not contention, and retrying will not help. */
  | { kind: 'refused'; reason: 'no-right' | 'immutable' }
  /** Asked, no answer yet. */
  | { kind: 'claiming' };

export interface RecordHoldClient {
  leaseClient: Pick<LeaseClient, 'state' | 'claim' | 'steal' | 'release' | 'subscribe'>;
  transport: Pick<Transport, 'state' | 'onMessage' | 'onStateChange'>;
  session: Pick<Session, 'presence'>;
  userId: string;
}

export interface RecordHoldOptions {
  client: RecordHoldClient;
  entity: string;
  recordId: string;
  /** Every change of view: repaint read-only/editable and the notice. */
  onView: (view: HoldView) => void;
  /**
   * This tab's hold was taken over by `by`. Keep the draft now — this runs
   * before the view turns `theirs`, so before anything repaints the form.
   */
  onTaken: (by: Holder) => Promise<void>;
  /** The record came free while this tab watched it, and this tab has it now: reload it. */
  onFreed: () => Promise<void>;
}

export interface OpenedState {
  ownedBy?: PresenceUser | null;
  ownedSince?: string | null;
  lockedBy?: PresenceUser | null;
}

export class RecordHold {
  readonly key: string;
  private _view: HoldView = { kind: 'claiming' };
  private readonly unsubscribers: Array<() => void> = [];
  private disposed = false;

  constructor(private readonly opts: RecordHoldOptions) {
    this.key = recordLeaseKey(opts.entity, opts.recordId);
    this.unsubscribers.push(opts.client.transport.onMessage((msg) => { void this.onMessage(msg); }));
    this.unsubscribers.push(opts.client.transport.onStateChange((s) => { void this.onTransport(s); }));
    this.unsubscribers.push(opts.client.leaseClient.subscribe(() => this.onLease()));
  }

  get view(): HoldView { return this._view; }

  /** The hold's token, while this tab has it — what a save carries. */
  leaseId(): string | undefined {
    const s = this.opts.client.leaseClient.state;
    return (s.phase === 'held' || s.phase === 'disconnected') && s.key === this.key ? s.leaseId : undefined;
  }

  /**
   * Take the record, or resume this tab's hold of it, or find out who has it.
   * `initial` is what the record's own read said; offline it is all there is.
   */
  async open(initial: OpenedState | undefined, offline: boolean): Promise<HoldView> {
    if (offline) {
      this.setView({ kind: 'offline', held: this.leaseId() !== undefined });
      return this._view;
    }
    const outcome = await this.opts.client.leaseClient.claim(this.key);
    if (outcome.ok) {
      this.setView({ kind: 'mine' });
    } else if (outcome.reason === 'denied') {
      this.setView(this.refusal(outcome, initial));
    } else if (outcome.reason === 'offline') {
      this.setView({ kind: 'offline', held: false });
    }
    return this._view;
  }

  /** Take it, whoever has it. The person taken over from keeps their draft (2026-09-12). */
  async takeOver(): Promise<LeaseOutcome> {
    const outcome = await this.opts.client.leaseClient.steal(this.key);
    if (outcome.ok) this.setView({ kind: 'mine' });
    else if (outcome.reason === 'denied') this.setView(this.refusal(outcome));
    return outcome;
  }

  /**
   * Before a save: a hold to save under, if one can be had. Offline there is
   * none to have and the save queues (ADR-0011) — `offline: true`.
   */
  async ensure(): Promise<{ ok: true; leaseId: string } | { ok: true; offline: true } | { ok: false; view: HoldView }> {
    const held = this.leaseId();
    if (held && this.opts.client.transport.state === 'open') return { ok: true, leaseId: held };
    if (this.opts.client.transport.state !== 'open') return { ok: true, offline: true };
    const outcome = await this.opts.client.leaseClient.claim(this.key);
    const leaseId = this.leaseId();
    if (outcome.ok && leaseId) {
      this.setView({ kind: 'mine' });
      return { ok: true, leaseId };
    }
    if (!outcome.ok && outcome.reason === 'offline') return { ok: true, offline: true };
    if (!outcome.ok && outcome.reason === 'denied') this.setView(this.refusal(outcome));
    return { ok: false, view: this._view };
  }

  /** Done: Cancel, close, or the form going away after a save. */
  release(): void {
    void this.opts.client.leaseClient.release(this.key);
  }

  dispose(): void {
    this.disposed = true;
    for (const unsub of this.unsubscribers) unsub();
  }

  holder(userId: string, since: string | null, online: boolean): Holder {
    const person = this.opts.client.session.presence(this.opts.entity, this.opts.recordId).find((p) => p.id === userId);
    return { userId, person, since, online };
  }

  private refusal(
    outcome: Extract<LeaseOutcome, { reason: 'denied' }>, initial?: OpenedState,
  ): HoldView {
    if (outcome.deniedBecause !== 'held') return { kind: 'refused', reason: outcome.deniedBecause };
    const h = this.holder(outcome.holder, outcome.since ?? initial?.ownedSince ?? null,
      outcome.online ?? Boolean(initial?.lockedBy));
    if (!h.person && initial?.ownedBy?.id === outcome.holder) h.person = initial.ownedBy;
    return { kind: 'theirs', holder: h, sameUser: outcome.holder === this.opts.client.userId };
  }

  private setView(view: HoldView): void {
    if (this.disposed) return;
    this._view = view;
    this.opts.onView(view);
  }

  private holdsIt(): boolean {
    const s = this.opts.client.leaseClient.state;
    return s.phase !== 'idle' && s.phase !== 'releasing' && s.key === this.key;
  }

  private onLease(): void {
    const s = this.opts.client.leaseClient.state;
    if (s.phase === 'held' && s.key === this.key && (this._view.kind === 'offline' || this._view.kind === 'claiming')) {
      this.setView({ kind: 'mine' });
    }
  }

  private async onMessage(msg: ServerMessage): Promise<void> {
    if (msg.t !== 'leaseTaken' && msg.t !== 'leaseFreed') return;
    if (msg.key !== this.key) return;
    const { entity, recordId } = parseFieldKey(msg.key);
    if (entity !== this.opts.entity || recordId !== this.opts.recordId) return;

    if (msg.t === 'leaseTaken') {
      const mineBefore = this._view.kind === 'mine' || (this._view.kind === 'offline' && this._view.held);
      // Taken off this tab. The lease client has already let go; the form
      // keeps its draft before the view changes and anything repaints.
      if (msg.stolenFrom !== undefined && mineBefore && !this.holdsIt()) {
        const by = this.holder(msg.holder, msg.since, msg.online);
        await this.opts.onTaken(by);
        this.setView({ kind: 'theirs', holder: by, sameUser: msg.holder === this.opts.client.userId });
        return;
      }
      // This tab's own hold, announced back (a resume, opening the record):
      // nothing changes hands.
      if (this.holdsIt()) return;
      if (this._view.kind === 'theirs' || this._view.kind === 'claiming') {
        this.setView({
          kind: 'theirs', holder: this.holder(msg.holder, msg.since, msg.online),
          sameUser: msg.holder === this.opts.client.userId,
        });
      }
      return;
    }

    // Free. A hold only ends by being released now, so this is real: take
    // it, and let the form reload what the last holder saved.
    if (this._view.kind !== 'theirs' || this.opts.client.transport.state !== 'open') return;
    const outcome = await this.opts.client.leaseClient.claim(this.key);
    if (outcome.ok) {
      this.setView({ kind: 'mine' });
      await this.opts.onFreed();
    } else if (outcome.reason === 'denied') {
      this.setView(this.refusal(outcome));
    }
  }

  private async onTransport(state: string): Promise<void> {
    if (state !== 'open') {
      if (this._view.kind === 'mine') this.setView({ kind: 'offline', held: true });
      return;
    }
    // Back. A hold this tab had is resumed by the lease client and turns the
    // view back to `mine` through `onLease`; one it never had — the form was
    // opened offline — is asked for now.
    if (this._view.kind === 'offline' && !this._view.held && !this.holdsIt()) {
      await this.open(undefined, false);
    }
  }
}

/**
 * The notice for a form that is not this tab's to edit, or is offline.
 * `thing` names it ("transaction", "entry"); `takeOverId` is the button the
 * form wires to `takeOver`.
 */
export function holdNoticeHtml(view: HoldView, thing: string, takeOverId: string): string {
  const takeOver = (label: string): string =>
    ` <button type="button" class="btn btn-sm btn-outline-primary ms-2" id="${takeOverId}">${label}</button>`;
  switch (view.kind) {
    case 'refused':
      return view.reason === 'immutable'
        ? `This ${thing} can no longer be edited.`
        : `You do not have permission to edit this ${thing}. Showing it read-only.`;
    case 'offline':
      return view.held
        ? `<strong>Offline.</strong> This ${thing} stays reserved for you while the connection is away — `
          + 'unless somebody takes it over. Saving keeps your changes on this device and sends them when it is back.'
        : `<strong>Offline — not reserved.</strong> Saving keeps your changes on this device and sends them when the `
          + 'connection is back; if somebody changed it meanwhile, you will be asked which version to keep.';
    case 'claiming':
      return '';
    case 'mine':
      return '';
    case 'theirs': {
      const h = view.holder;
      const ago = h.since ? escapeHtml(timeAgo(h.since)) : '';
      if (view.sameUser) {
        return (h.online
          ? `You have this ${thing} open in another tab. Showing a live, read-only view here.`
          : `You started editing this ${thing} in another tab${ago ? ` ${ago}` : ''} and haven't finished there.`)
          + takeOver('Edit here instead');
      }
      const who = h.person ? `${presenceDotHtml(h.person)} <strong>${escapeHtml(h.person.name)}</strong>` : 'Someone else';
      return (h.online
        ? `${who} is editing this ${thing} right now. Showing a live, read-only view — it becomes editable `
          + 'on its own when they finish.'
        : `${who} started editing this ${thing}${ago ? ` ${ago}` : ''} and hasn't finished. `
          + 'Read-only until you take it over.')
        + takeOver('Take over');
    }
  }
}
